export type HelpBlock =
  | {
    type: 'paragraph';
    text: string;
  }
  | {
    type: 'bullets';
    items: string[];
  }
  | {
    type: 'note';
    title: string;
    text: string;
  }
  | {
    type: 'parameterGrid';
    items: {
      name: string;
      description: string;
      effect: string;
    }[];
  };

export interface HelpSection {
  id: string;
  title: string;
  blocks: HelpBlock[];
}

export interface HelpTopic {
  id: string;
  label: string;
  title: string;
  intro: string;
  sections: HelpSection[];
}

export const helpTopics: HelpTopic[] = [
  {
    id: 'scal',
    label: 'SCAL',
    title: 'Special Core Analysis',
    intro: 'SCAL turns representative core samples into rock-fluid measurements that can be used in reservoir interpretation, saturation modeling, and simulation inputs.',
    sections: [
      {
        id: 'what-is-scal',
        title: 'What SCAL is',
        blocks: [
          {
            type: 'paragraph',
            text: 'Special Core Analysis is a laboratory workflow for measuring how reservoir rock interacts with fluids under controlled conditions. It goes beyond routine porosity and permeability by describing flow behavior, saturation endpoints, capillary pressure, and relative permeability.',
          },
          {
            type: 'bullets',
            items: [
              'It helps characterize reservoir quality and pore-throat behavior.',
              'It provides input curves for static and dynamic reservoir models.',
              'It supports interpretation of water, oil, and gas movement through porous media.',
            ],
          },
        ],
      },
      {
        id: 'why-scal-matters',
        title: 'Why it is performed',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Reservoir characterization: connect core-scale measurements to rock types and flow units.',
              'Fluid flow understanding: describe how saturation changes influence pressure and mobility.',
              'Simulation inputs: provide capillary pressure and relative permeability curves used by reservoir models.',
            ],
          },
        ],
      },
      {
        id: 'what-scal-provides',
        title: 'What it provides',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Capillary pressure relationships between pressure and water saturation.',
              'Relative permeability curves for water-oil and gas-oil systems.',
              'Rock-fluid behavior that helps calibrate contacts, transition zones, and saturation-height functions.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'capillary-pressure',
    label: 'Capillary Pressure',
    title: 'Capillary Pressure Analysis',
    intro: 'Capillary Pressure Analysis explains how pressure changes with water saturation and helps users calibrate saturation behavior for reservoir modeling.',
    sections: [
      {
        id: 'what-is-pc',
        title: 'What capillary pressure is',
        blocks: [
          {
            type: 'paragraph',
            text: 'Capillary pressure is the pressure difference between non-wetting and wetting phases in the pore system. In many oil-water workflows, it describes how much pressure is needed for oil to displace water at a given saturation.',
          },
          {
            type: 'bullets',
            items: [
              'Higher Pc generally means the rock is holding water more strongly or smaller pores are being invaded.',
              'Lower Pc usually occurs near high water saturation, where fewer pore throats have been drained.',
              'The shape of the Pc curve reflects pore-throat distribution and rock quality.',
            ],
          },
        ],
      },
      {
        id: 'pc-sw',
        title: 'Pc and Sw relationship',
        blocks: [
          {
            type: 'paragraph',
            text: 'Water saturation (Sw) is plotted against capillary pressure to show how the sample desaturates. As capillary pressure increases, water saturation usually decreases toward irreducible water saturation.',
          },
          {
            type: 'bullets',
            items: [
              'The high-Sw end controls entry behavior and early drainage.',
              'The low-Sw end controls irreducible water behavior and the upper end of the pressure range.',
              'A smooth, monotonic trend is usually easier to model and validate than noisy or crossing points.',
            ],
          },
        ],
      },
      {
        id: 'why-analysis-matters',
        title: 'Why the analysis matters',
        blocks: [
          {
            type: 'bullets',
            items: [
              'It supports fluid distribution estimates in transition zones.',
              'It helps calibrate saturation-height behavior for static models.',
              'It gives users a way to compare measured data against fitted model curves before using results in correlations or simulation.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'liao',
    label: 'Liao',
    title: 'Liao Methodology',
    intro: 'The Liao workflow in this application fits laboratory capillary pressure data, applies reservoir-condition corrections, estimates endpoint behavior, and prepares values for saturation modeling and correlations.',
    sections: [
      {
        id: 'liao-overview',
        title: 'Overview',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Liao method is used here as an integrated capillary pressure modeling workflow. It converts measured Pc and Sw data into corrected and normalized forms, fits the model parameters, and estimates endpoints that are useful for initial fluid saturation interpretation.',
          },
          {
            type: 'note',
            title: 'Workflow focus',
            text: 'Use the Liao workflow when the goal is to transform measured laboratory Pc data into a reservoir-condition model curve that can support initial saturation interpretation.',
          },
          {
            type: 'bullets',
            items: [
              'Use it when measured Pc data needs a calibrated model curve rather than only raw laboratory points.',
              'It helps correct lab measurements toward reservoir behavior through fluid-system and rock-fluid inputs.',
              'It produces values that can be compared across samples, flow units, and FZI trends.',
            ],
          },
        ],
      },
      {
        id: 'liao-inputs',
        title: 'Input variables',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Core Sample Length',
                description: 'Physical length of the core plug used in the laboratory experiment, expressed in centimeters. This is the actual plug dimension as cut from the whole core.',
                effect: 'Does not affect capillary pressure curve fitting or reservoir corrections. It is used as the thickness weight (h) in the Stratigraphic Lorenz chart on the Zones page — φ×h and k×h integrals. If left blank, the Lorenz calculation assigns a unit weight of 1 to this sample.',
              },
              {
                name: 'Porosity',
                description: 'Fraction of the rock volume available for fluids.',
                effect: 'Influences pore-volume scaling and rock-quality interpretation.',
              },
              {
                name: 'Permeability',
                description: 'Ability of the rock to transmit fluids.',
                effect: 'Used with porosity to estimate RQI/FZI and compare flow behavior across samples.',
              },
              {
                name: 'Swir',
                description: 'Irreducible water saturation, the lower water-saturation endpoint.',
                effect: 'Controls saturation normalization; increasing Swir compresses the normalized SwD range.',
              },
              {
                name: 'Salinity',
                description: 'Brine salinity used in rock-fluid correction behavior.',
                effect: 'Changes the correction factor applied to saturation and pressure terms.',
              },
              {
                name: 'Phi_res / Phi_lab factor',
                description: 'Porosity correction between laboratory and reservoir conditions.',
                effect: 'Adjusts measured saturation and pressure before model fitting.',
              },
              {
                name: 'Qv',
                description: 'Cation exchange capacity, representing clay/electrochemical interaction with brine.',
                effect: 'Affects the correction factor used before normalized Liao calculations.',
              },
              {
                name: 'Water gradient',
                description: 'Pressure gradient of water in reservoir units (psi/ft). Default: 0.486 psi/ft.',
                effect: 'Used with oil gradient to translate pressure behavior into height or saturation-depth context.',
              },
              {
                name: 'Oil gradient',
                description: 'Pressure gradient of oil in reservoir units (psi/ft). Default: 0.373 psi/ft.',
                effect: 'Controls the hydrocarbon-water gradient contrast used in height calculations.',
              },
              {
                name: 'Lab system',
                description: 'Fluid system used during laboratory measurement, such as air-brine or oil-brine.',
                effect: 'Defines the lab interfacial-tension conversion basis.',
              },
              {
                name: 'Reservoir system',
                description: 'Fluid system expected under reservoir conditions.',
                effect: 'Defines the reservoir conversion basis for corrected Pc values.',
              },
            ],
          },
        ],
      },
      {
        id: 'liao-curves',
        title: 'Curves',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Capillary Pressure Curve (Pc): measured pressure values that describe how strongly phases are held in the pore system.',
              'Water Saturation (Sw): measured water saturation values paired with Pc points.',
              'Together, Pc and Sw define the measured drainage trend that the Liao model tries to reproduce after correction and normalization.',
            ],
          },
        ],
      },
      {
        id: 'liao-parameters',
        title: 'Model parameters',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'A',
                description: 'A fitted coefficient used in the Liao pressure transform.',
                effect: 'Changing A shifts the pressure-side fit and can move the model curve closer to or away from measured PcD points.',
              },
              {
                name: 'B',
                description: 'A fitted coefficient used in the saturation-side transform.',
                effect: 'Changing B alters the curvature and slope response along the SwD axis.',
              },
              {
                name: 'Pce',
                description: 'Entry-pressure term in normalized pressure space.',
                effect: 'Higher Pce lifts the low-pressure end of the model; lower Pce allows the curve to start closer to zero pressure.',
              },
              {
                name: 'Large Derivative',
                description: 'Endpoint behavior control used to estimate true saturation and pressure limits.',
                effect: 'More negative values steepen endpoint behavior and can change the estimated true Pc maximum and true Sw endpoint.',
              },
            ],
          },
        ],
      },
      {
        id: 'find-best-values',
        title: 'Find Best Values',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Find Best Values action runs a curve-fitting search for Liao parameters that better match the corrected experimental data within the selected limits.',
          },
          {
            type: 'bullets',
            items: [
              'Expect A and B to update after optimization.',
              'Check whether the modeled curve aligns with the measured Data SCAL points.',
              'If the fit is poor, review input curves, selected limits, Pce, and endpoint behavior rather than accepting the result automatically.',
            ],
          },
        ],
      },
      {
        id: 'liao-charts',
        title: 'Charts in the Capillary Pressure tab',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Capillary Pressure chart: compares measured Data SCAL points with the fitted Pc Liao curve and, when available, Brooks-Corey.',
              'Finding A and B values: shows the transformed fitting space used to judge whether the selected model parameters form a reasonable trend.',
              'Primary pc curve: shows PcD versus SwD so users can inspect normalized curve behavior and endpoint consistency.',
            ],
          },
          {
            type: 'note',
            title: 'What to look for',
            text: 'Good calibration is not only a low error value. The model should follow the measured trend, respect endpoints, and avoid unrealistic curvature near Swir or high water saturation.',
          },
        ],
      },
      {
        id: 'liao-correlation',
        title: 'Liao results for correlation',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Generated values such as Best A, Best B, lambda variable, Pc Max, true endpoints, FZI, and RQI can be used for sample comparison.',
              'FZI correlation helps group samples with similar pore-system quality.',
              'Cross-sample comparison helps identify rock types, outliers, and samples that need input review.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'brooks-corey',
    label: 'Brooks-Corey',
    title: 'Brooks-Corey Methodology',
    intro: 'Brooks-Corey is a compact capillary-pressure model. It uses the signed exponent m; the classic positive pore-size distribution index is λBC = −1/m.',
    sections: [
      {
        id: 'bc-overview',
        title: 'Overview',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Brooks-Corey model is commonly used when users need a simple and interpretable capillary pressure relationship. In this application, it provides a second modeling view that can be compared against measured data and Liao results.',
          },
          {
            type: 'bullets',
            items: [
              'Use it as a practical curve model when the data trend can be represented with fewer parameters.',
              'Compare it against Liao when deciding whether added flexibility is necessary.',
              'Review selected points carefully because the fitted exponent m, and therefore λBC, are sensitive to the chosen fitting interval.',
            ],
          },
        ],
      },
      {
        id: 'bc-lambda',
        title: 'Fitted exponent m and pore-size index λBC',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Fitted capillary-pressure exponent (m)',
                description: 'The signed log-log exponent in Pc = Pe · Swn^m. It is negative for a drainage curve.',
                effect: 'More-negative m makes Pc rise more sharply as saturation approaches Swir.',
              },
              {
                name: 'Brooks-Corey pore-size distribution index (λBC)',
                description: 'The classic positive Brooks-Corey pore-size distribution index, λBC = −1/m.',
                effect: 'It provides a familiar geological interpretation of the pore-size distribution. Smaller λBC corresponds to a sharper Pc rise toward Swir.',
              },
            ],
          },
        ],
      },
      {
        id: 'bc-behavior',
        title: 'Model behavior compared with Liao',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Brooks-Corey is simpler and easier to interpret because it relies on fewer fitted terms.',
              'Liao is more flexible and includes additional correction and endpoint behavior.',
              'Brooks-Corey is useful for quick comparison, while Liao is useful when reservoir correction and endpoint modeling are central to the workflow.',
            ],
          },
        ],
      },
      {
        id: 'bc-inputs-curves',
        title: 'Inputs and curves',
        blocks: [
          {
            type: 'bullets',
            items: [
              'The same measured Pc and Sw points form the base input curve.',
              'Swir and Pc Max define important endpoints for normalization and plotting.',
              'Point selection matters: excluding noisy or non-representative points can materially change m and the derived λBC.',
              'Lab and reservoir systems still matter when corrected Pc values are compared with other analysis outputs.',
            ],
          },
        ],
      },
      {
        id: 'bc-charts',
        title: 'Charts and interpretation',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Use the Brooks-Corey curve to check whether a simpler model can explain the measured trend.',
              'Compare selected and unselected Data SCAL points to understand which measurements are driving m and the derived λBC.',
              'Look for systematic deviation at high Sw or near Swir; those regions often reveal whether the model is too simple for the sample.',
            ],
          },
        ],
      },
    ],
  },
];
